// src/pages/CaseStudies.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/CaseStudies.css";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

const CaseStudies = () => {
    const [caseStudies, setCaseStudies] = useState([]);
  const [featuredEnabled, setFeaturedEnabled] = useState(false);
  const [featuredId, setFeaturedId] = useState("");

  const [heroTitle, setHeroTitle] = useState("Case Studies");
  const [heroDescription, setHeroDescription] = useState(
    "A closer look at selected projects, the challenges behind them, and how I approached the final solution."
  );
  const [heroImage, setHeroImage] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch case studies
  useEffect(() => {
    const itemsRef = collection(
      db,
      "pageContent",
      "caseStudies",
      "items"
    );

    const q = query(itemsRef, orderBy("order", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data(),
          }))
          .filter((item) => item.published !== false);

        setCaseStudies(items);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching case studies:", err);
        setCaseStudies([]);
        setError("Failed to load case studies.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

    // Fetch case study settings
  useEffect(() => {
    const settingsRef = doc(db, "pageContent", "caseStudies");

    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();

          setFeaturedEnabled(Boolean(data.featuredEnabled));
          setFeaturedId(data.featuredId || "");

          setHeroTitle(data.heroTitle || "Case Studies");
          setHeroDescription(
            data.heroDescription ||
              "A closer look at selected projects, the challenges behind them, and how I approached the final solution."
          );
          setHeroImage(data.heroImage || "");
        } else {
          setFeaturedEnabled(false);
          setFeaturedId("");

          setHeroTitle("Case Studies");
          setHeroDescription(
            "A closer look at selected projects, the challenges behind them, and how I approached the final solution."
          );
          setHeroImage("");
        }
      },
      (err) => {
        console.error("Error fetching case study settings:", err);
      }
    );

    return () => unsubscribe();
  }, []);

  const featuredCaseStudy =
    featuredEnabled && featuredId
      ? caseStudies.find((item) => item.id === featuredId)
      : null;

  return (
    <div className="case-studies-container">

  {/* Hero Section */}
    <section
      className="case-studies-hero"
        style={
          heroImage
            ? {
                backgroundImage: `url(${heroImage})`,
              }
            : undefined
        }
      >
        <div className="case-studies-overlay" />

        <div className="case-studies-hero-content">
          <h1>{heroTitle}</h1>
          <p>{heroDescription}</p>
        </div>
      </section>

      <div className="page-body-with-doodles">

        {/* Loading */}
      {loading ? (
        <section className="case-studies-grid-section">
          <div className="case-studies-section-heading">
            <span>SELECTED PROJECTS</span>
            <h2>All Case Studies</h2>
          </div>

          <p className="case-studies-empty">
            Loading case studies...
          </p>
        </section>
      ) : error ? (
        /* Error */
        <section className="case-studies-grid-section">
          <p className="case-studies-empty">{error}</p>
        </section>
      ) : caseStudies.length === 0 ? (
        /* Empty State */
        <section className="case-studies-grid-section">
          <div className="case-studies-section-heading">
            <span>SELECTED PROJECTS</span>
            <h2>All Case Studies</h2>
          </div>
          <p className="case-studies-empty">
          No case studies available yet.
          <br />
          Check back soon for selected project stories.
          </p>
        </section>
      ) : (
        <>
          {/* Featured Case Study */}
          {featuredCaseStudy && (
            <section className="case-studies-featured-section">
              <div className="case-studies-section-heading">
                <span>FEATURED CASE STUDY</span>
                <h2>Selected Work</h2>
              </div>

              <div className="case-study-featured-card">
                <div className="case-study-featured-image">
                  <img
                    src={featuredCaseStudy.coverImage}
                    alt={featuredCaseStudy.title}
                  />
                </div>

                <div className="case-study-featured-content">
                  {featuredCaseStudy.category && (
                    <span className="case-study-category">
                      {featuredCaseStudy.category}
                    </span>
                  )}

                  <h3>{featuredCaseStudy.title}</h3>

                  {featuredCaseStudy.description && (
                    <p>{featuredCaseStudy.description}</p>
                  )}

                  <Link
                    to={`/case-studies/${featuredCaseStudy.slug}`}
                    className="case-study-read-button"
                  >
                    Read Case Study
                    <span>→</span>
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* All Case Studies */}
          <section className="case-studies-grid-section">
            <div className="case-studies-section-heading">
              <span>SELECTED PROJECTS</span>
              <h2>All Case Studies</h2>
            </div>

            <div className="case-studies-grid">
              {caseStudies.map((caseStudy) => (
                <article
                  className="case-study-card"
                  key={caseStudy.id}
                >
                  <div className="case-study-card-image">
                    <img
                      src={caseStudy.coverImage}
                      alt={caseStudy.title}
                    />
                  </div>

                  <div className="case-study-card-content">
                    {caseStudy.category && (
                      <span className="case-study-category">
                        {caseStudy.category}
                      </span>
                    )}

                    <h3>{caseStudy.title}</h3>

                    {caseStudy.description && (
                      <p>{caseStudy.description}</p>
                    )}

                    <Link
                      to={`/case-studies/${caseStudy.slug}`}
                      className="case-study-card-link"
                    >
                      Read Case Study <span>→</span>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
                    </section>
        </>
      )}
            <div className="case-studies-cta">
        <Link
          to="/achievements"
          className="case-studies-cta-button"
        >
          Explore My Achievements
        </Link>
      </div>

      </div>

      <Footer />
    </div>
  );
};

export default CaseStudies;